import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClientConfigurationService } from 'src/app/core/client-configuration/client-configuration.service';
import { Appointment } from 'src/app/core/models/appointment.model';
import { Charge } from 'src/app/core/models/charge.model';
import { AppointmentClearCode, ClearAppointment } from 'src/app/core/models/ticket-clear-code-type.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { ClearCodeService } from 'src/app/core/services/clear-code/clear-code.service';
import { TicketStatusTransitionsService } from 'src/app/core/services/ticket-status-transitions/ticket-status-transitions.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { SyncService } from 'src/app/core/sync/sync.service';
import { WorkforceForm } from 'src/app/core/models/workforce-form.model';

interface ClearCodeTab {
	href: string;
	title: string;
	hasError: boolean;
	feature?: string;
	isInvalid: (entry: AppointmentClearCode) => Promise<boolean>;
}

@Component({
	selector: 'app-appointment-clear-codes',
	templateUrl: './appointment-clear-codes.component.html',
	styleUrls: ['./appointment-clear-codes.component.scss'],
})
export class AppointmentClearCodesComponent {

	pageTitle = 'Clear Codes';

	appointment: Appointment;
	activeTab: string;
	tabs = [
		{
			href: 'codes',
			title: 'Codes',
			hasError: false,
			isInvalid: async (entry: AppointmentClearCode): Promise<boolean> => {
				if (!entry) {
					return true;
				}

				const clearCodeTypes = await this.clearCodeService.getTicketClearCodeTypes(this.appointment.ticketCodeId);
				let hasError = false;
				if (!!clearCodeTypes && entry.clearCode.codes.length !== clearCodeTypes.types.length) {
					hasError = true;
				}
				return hasError;
			}
		},
		{
			href: 'notes',
			title: 'Notes',
			hasError: false,
			isInvalid: async (entry: AppointmentClearCode): Promise<boolean> => {
				if (!entry) {
					return true;
				}

				const MIN_NOTE_LENGTH = 5;
				let hasError = false;
				if (entry.clearCode.note.trim().length < MIN_NOTE_LENGTH) {
					hasError = true;
				}

				return hasError;
			},
		},
		{
			href: 'charges',
			title: 'Charges',
			hasError: false,
			isInvalid: async (entry: AppointmentClearCode): Promise<boolean> => {
				if (!entry) {
					return true;
				}

				return false;
			},
		},
		{
			href: 'time',
			title: 'Time',
			hasError: false,
			feature: 'ShowTimeCodeInClearCodeTab',
			isInvalid: async (entry: AppointmentClearCode): Promise<boolean> => {
				if (!entry) {
					return true;
				}

				return false;
			},
		},
		{
			href: 'forms',
			title: 'Forms',
			hasError: false,
			feature: 'Enable_WF_Forms',
			isInvalid: async (entry: AppointmentClearCode): Promise<boolean> => {
				if (!entry) {
					return true;
				}

				const appointment = await this.appointmentService.getLocalAppointment(this.appointment.id);
				const appointmentFormIds = appointment.forms.map(f => f.id);
				const store = this.storage['forms'];
				const workforceForms: WorkforceForm[] = await store.getAll();
				const requiredFormIds = workforceForms.filter(f => f.requiredToClear && f.requiredInMobiletech && f.availabeTicketCodes.includes(appointment.ticketCodeId)).map(f => f.id);

				let hasError = false;
				for (let i = 0; i < requiredFormIds.length; i++) {
					if (!appointmentFormIds.includes(requiredFormIds[i])) {
						hasError = true;
						break;
					}
				}
				return hasError;
			},
		},
	] as ClearCodeTab[];
	canClear = true;
	destroyed$: Subject<boolean>;

	constructor(
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private appointmentService: AppointmentService,
		private clearCodeService: ClearCodeService,
		private alertController: AlertController,
		private storage: LocalStorageService,
		private ticketStatusTransition: TicketStatusTransitionsService,
		private syncService: SyncService,
		public clientConfigurationService: ClientConfigurationService,
	) { }

	async ionViewWillEnter(): Promise<void> {
		this.destroyed$ = new Subject();
		await this.removeTabsByClientConfiguration();

		await this.getAppointmentFromUrl();

		this.refreshOnTicketStatusChange();
		this.clearStateOnFullSync();
	}

	ionViewWillLeave(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	// Refresh appointment when ticket status changes
	private refreshOnTicketStatusChange() {
		this.ticketStatusTransition.updates$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(appointment => {
				if (appointment != null) {
					this.getAppointmentFromUrl();
				}
			});
	}

	// Reset on reltimeTabTimeCodesoad configuration
	private clearStateOnFullSync() {
		this.syncService.isBlockingSync$
			.pipe(takeUntil(this.destroyed$))
			.subscribe(isBlocking => {
				if (isBlocking) {
					this.clearCodeService.clearTable();
				}
			});
	}

	async removeTabsByClientConfiguration(): Promise<void> {
		await Promise.all(this.tabs.map(async (tab, index) => {
			if (!tab?.feature) {
				return;
			}

			if (!(await this.clientConfigurationService.hasFeature(tab.feature))) {
				this.tabs.splice(index, 1);
			}
		}));
	}

	async getAppointmentFromUrl(): Promise<void> {
		const id = this.activatedRoute.snapshot.parent.params?.id;
		const appointment = await this.storage.appointments.getItem<Appointment>(id);
		if (!appointment) {
			throw new Error('Error: Could not load appointment');
		}

		this.appointment = appointment;
	}

	/**
	 * Appointment must be in clearing status
	 * All tabs must pass validation
	 * Update isTicketEndStatus to true
	 * Send API request and remove saved clearCode data
	 * Return user to appointment list
	 */
	async onClickClearAppointment(): Promise<void> {
		if (this.appointment.ticketStatusDesc !== 'Clearing') {
			await this.alertAppointmentNotInClearing();
			return;
		}

		const entry = await this.appointmentService.getClearCodeState(this.appointment.id);
		this.canClear = await this.checkIfCanClear(entry);
		if (!this.canClear) {
			return;
		}

		this.appointment.ticketStatusIsEndStatus = true;
		await this.storage.appointments.setItem(this.appointment.id, this.appointment);

		await this.appointmentService.clearAppointment(new ClearAppointment({
			appointmentId: this.appointment.id,
			ticketClearCodeIds: entry.clearCode.codes.map(set => set.ticketClearCodeOption.id),
			note: entry.clearCode.note,
			timestamp: moment().toISOString(true),
			charges: entry.clearCode.charges.map(x => this.formatAdditionalCharge(x))
		}));

		this.ticketStatusTransition.setUpdated(this.appointment);
		this.router.navigate(['/appointments']);
	}

	formatAdditionalCharge(charge: Charge) {
		return {
			additionalChargeId: charge.chargeCode.id,
			qty: charge.qty,
			billable: charge.billable
		};
	}

	async checkIfCanClear(entry: AppointmentClearCode): Promise<boolean> {
		const hasInvalidTab = await this.checkIfHasInvalidTab(entry);
		if (hasInvalidTab) {
			await this.alertClearCodesInvalid();
			return false;
		}

		return true;
	}

	async checkIfHasInvalidTab(entry: AppointmentClearCode) {
		let hasInvalidTab = false;
		let index = 0;
		for (const tab of this.tabs) {
			const isInvalid = await tab.isInvalid(entry);
			if (isInvalid) {
				hasInvalidTab = true;
			}

			// Update tab state
			this.tabs[index].hasError = isInvalid;
			index++;
		}

		return hasInvalidTab;
	}

	async alertClearCodesInvalid(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Error',
			message: 'Failed to clear the appointment. Please complete the required fields for the tabs in red.',
			buttons: ['OK'],
		});

		await alert.present();
	}

	async alertAppointmentNotInClearing(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Error',
			message: `Appointment status must set to 'Clearing' before it may be cleared.`,
			buttons: ['OK'],
		});

		await alert.present();
	}

}
